import type {
  HttpUserPrompt,
  PromptTextPart,
  PromptWandbRunPart,
} from '@wandb/weave/lib/WbAgent/promptParts';

interface ShellResult {
  stdout?: string;
  stderr?: string;
  exit_code?: number;
}

export const isShellResult = (value: unknown): value is ShellResult => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const result = value as Record<string, unknown>;
  return (
    ('stdout' in result || 'stderr' in result || 'exit_code' in result) &&
    (result.stdout === undefined || typeof result.stdout === 'string') &&
    (result.stderr === undefined || typeof result.stderr === 'string') &&
    (result.exit_code === undefined || typeof result.exit_code === 'number')
  );
};

export const extractPythonCodeBlock = (command: string): string | null => {
  const match = command.match(
    /^python\s+-\s+<<-?['"]?(\w+)['"]?\n([\s\S]*?)\n\1\s*$/
  );
  return match ? match[2] : null;
};

const PROMPT_CONTEXT_RE = /^<PromptContext>[\s\S]*?<\/PromptContext>\s*/;
const INLINE_RUN_ID_RE =
  /\{run_id: "[^"]*"(?:,\s*run_display_name: "([^"]*)")?\}/g;
const CONTEXT_RUN_RE =
  /\{run_id: "([^"]*)"(?:,\s*run_display_name: "([^"]*)")?\}/g;

export function encodeMention(name: string, displayName: string): string {
  return `{run_id: "${name}", run_display_name: "${displayName}"}`;
}

export function encodePromptContext(
  runs: ReadonlyArray<{name: string; displayName: string}>,
  userText: string
): string {
  if (runs.length === 0) {
    return userText;
  }
  const contextBlock = runs
    .map(r => encodeMention(r.name, r.displayName))
    .join('\n');
  return `<PromptContext> The following data is provided as supplementary to the user prompt\n${contextBlock}\n</PromptContext>\n${userText}`;
}

export function decodePromptForThreadTitle(raw: HttpUserPrompt): string {
  let text: string;
  if (typeof raw === 'string') {
    text = raw;
  } else {
    // First text part marks user content per assemblePromptParts;
    // earlier parts are metadata (route context, attached run pills).
    const start = raw.findIndex(p => p.type === 'text');
    text = (start < 0 ? [] : raw.slice(start))
      .map(p => {
        if (p.type === 'text') {
          return (p as PromptTextPart).text;
        }
        if (p.type === 'wandb_run') {
          return `@${(p as PromptWandbRunPart).run_name}`;
        }
        return '';
      })
      .join(' ');
  }

  const cleaned = text
    .replace(PROMPT_CONTEXT_RE, '')
    .replace(INLINE_RUN_ID_RE, (_, displayName) =>
      displayName ? `@${displayName}` : ''
    )
    .replace(/  +/g, ' ')
    .trim();

  if (cleaned) {
    return cleaned;
  }
  // Fallback for historical threads that were created using the legacy API
  const contextMatch = text.match(PROMPT_CONTEXT_RE);
  if (contextMatch) {
    const contextNames: string[] = [];
    for (const m of contextMatch[0].matchAll(CONTEXT_RUN_RE)) {
      if (m[2]) {
        contextNames.push(m[2]);
      }
    }
    if (contextNames.length > 0) {
      return contextNames.map(name => `@${name}`).join(', ');
    }
  }
  return '';
}

export function decodePromptForMessageDisplay(raw: string): string {
  return raw
    .replace(PROMPT_CONTEXT_RE, '')
    .replace(INLINE_RUN_ID_RE, (_, displayName) =>
      displayName ? `\`${displayName}\`` : ''
    );
}
